import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash2, Search, Eye } from "lucide-react";
import { toast } from "sonner";
import FileAttachmentField, { AttachmentBadge } from "@/components/FileAttachmentField";
import ExportButton from "@/components/ExportButton";

/**
 * Reusable CRUD table page.
 * fields: array of { key, label, type?, options?, colSpan?, hideInTable?, hideInForm?, format?, badge?, mono?, textarea? }
 * type: text | number | date | textarea | select
 * badge: (val) => JSX for status columns
 */
export default function GenericCrudPage({
  resource, title, subtitle, moduleKey, testidPrefix,
  fields, defaultItem, statusField, statusOptions = [], searchKeys = [], filterFields = [],
  summaryCards = [],
}) {
  const qc = useQueryClient();
  const { canWrite } = useAuth();
  const canEdit = canWrite(moduleKey);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [page, setPage] = useState(1);
  const PER = 10;

  const { data = [], isLoading } = useQuery({
    queryKey: [resource],
    queryFn: async () => (await apiClient.get(`/${resource}`)).data,
  });

  const filtered = useMemo(() => {
    return data.filter((r) => {
      const matchQ = !q || searchKeys.some(k => (r[k] || "").toString().toLowerCase().includes(q.toLowerCase()));
      const matchF = Object.entries(filters).every(([k, v]) => !v || v === "ALL" || r[k] === v);
      return matchQ && matchF;
    });
  }, [data, q, filters, searchKeys]);

  const paged = useMemo(() => filtered.slice((page-1)*PER, page*PER), [filtered, page]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER));

  const mSave = useMutation({
    mutationFn: async (b) => b.id ? (await apiClient.put(`/${resource}/${b.id}`, b)).data : (await apiClient.post(`/${resource}`, b)).data,
    onSuccess: () => { qc.invalidateQueries({queryKey:[resource]}); qc.invalidateQueries({queryKey:["jktof-dashboard"]}); qc.invalidateQueries({queryKey:["dashboard-stats"]}); qc.invalidateQueries({queryKey:["recent-logs"]}); toast.success("Data disimpan"); setOpen(false); },
    onError: (e) => toast.error(e.response?.data?.detail || "Gagal"),
  });
  const mDel = useMutation({
    mutationFn: async (id) => (await apiClient.delete(`/${resource}/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({queryKey:[resource]}); qc.invalidateQueries({queryKey:["jktof-dashboard"]}); toast.success("Data dihapus"); },
  });

  const summaryVals = useMemo(() => summaryCards.map(sc => ({ ...sc, value: sc.compute(data) })), [data, summaryCards]);

  const renderCell = (r, f) => {
    if (f.type === "file") {
      const v = r[f.key]; const obj = v && typeof v === "object" ? v : null;
      return <AttachmentBadge path={obj?.path} name={obj?.name} />;
    }
    if (f.badge) return f.badge(r[f.key]);
    if (f.format) return f.format(r[f.key], r);
    if (f.mono) return <span className="mono text-xs">{r[f.key] || "—"}</span>;
    return r[f.key] || "—";
  };

  return (
    <div className="space-y-6" data-testid={`${testidPrefix}-page`}>
      <div>
        {subtitle && <div className="text-[11px] font-semibold uppercase tracking-widest text-[#00A651]">{subtitle}</div>}
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>{title}</h1>
      </div>

      {summaryVals.length > 0 && (
        <div className={`grid grid-cols-2 lg:grid-cols-${Math.min(summaryVals.length, 4)} gap-3`}>
          {summaryVals.map(sc => (
            <Card key={sc.label} className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sc.tone}`}><sc.icon className="w-5 h-5"/></div>
              <div><div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{sc.label}</div>
                <div className="text-xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>{sc.value}</div></div>
            </CardContent></Card>
          ))}
        </div>
      )}

      <Card className="border-slate-200"><CardContent className="p-4 space-y-4">
        <div className="flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between">
          <div className="flex flex-col sm:flex-row gap-2 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Cari..." className="pl-9" value={q} onChange={(e)=>{setQ(e.target.value); setPage(1);}} data-testid={`${testidPrefix}-search`}/>
            </div>
            {filterFields.map(ff => (
              <Select key={ff.key} value={filters[ff.key] || "ALL"} onValueChange={(v)=>{setFilters({...filters, [ff.key]: v}); setPage(1);}}>
                <SelectTrigger className="w-full sm:w-44" data-testid={`${testidPrefix}-filter-${ff.key}`}><SelectValue placeholder={ff.label}/></SelectTrigger>
                <SelectContent><SelectItem value="ALL">Semua {ff.label}</SelectItem>{ff.options.map(o=><SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            ))}
          </div>
          {canEdit && <Button onClick={()=>{setEditing({...defaultItem}); setOpen(true);}} className="bg-[#00A651] hover:bg-[#008741]" data-testid={`${testidPrefix}-add-btn`}><Plus className="w-4 h-4 mr-1"/> Tambah</Button>}
          <ExportButton rows={filtered} columns={fields.filter(f=>!f.hideInTable && f.type !== "file").map(f => ({
            key: f.key, label: f.label,
            format: f.badge ? (v)=>String(v||"") : f.format ? (v,r)=>{ try { const el = f.format(v,r); return typeof el === "string" ? el : (v ?? ""); } catch { return v ?? ""; } } : undefined,
          }))} title={title} filename={testidPrefix} testid={`${testidPrefix}-export-btn`} />
        </div>

        <div className="rounded-lg border border-slate-200 overflow-hidden overflow-x-auto">
          <Table className="fodm-table">
            <TableHeader className="bg-slate-50">
              <TableRow>
                {fields.filter(f=>!f.hideInTable).map(f => <TableHead key={f.key}>{f.label}</TableHead>)}
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? Array.from({length:5}).map((_,i)=>(<TableRow key={i}><TableCell colSpan={fields.length+1}><Skeleton className="h-6 w-full"/></TableCell></TableRow>))
              : paged.length === 0 ? <TableRow><TableCell colSpan={fields.length+1} className="text-center py-8 italic text-slate-500 text-sm">Tidak ada data.</TableCell></TableRow>
              : paged.map(r => (
                <TableRow key={r.id} data-testid={`${testidPrefix}-row`}>
                  {fields.filter(f=>!f.hideInTable).map(f => <TableCell key={f.key} className={f.className || ""}>{renderCell(r, f)}</TableCell>)}
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={()=>setViewing(r)}><Eye className="w-4 h-4"/></Button>
                      {canEdit && <>
                        <Button size="sm" variant="ghost" onClick={()=>{setEditing({...r}); setOpen(true);}}><Pencil className="w-4 h-4"/></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-red-600"><Trash2 className="w-4 h-4"/></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Hapus data?</AlertDialogTitle><AlertDialogDescription>Data akan dihapus. Untuk data keuangan disarankan diarsipkan.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={()=>mDel.mutate(r.id)}>Hapus</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between text-xs text-slate-500">
            <div>Menampilkan {(page-1)*PER + 1}–{Math.min(page*PER, filtered.length)} dari {filtered.length}</div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>Sebelumnya</Button>
              <Button size="sm" variant="outline" disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>Berikutnya</Button>
            </div>
          </div>
        )}
      </CardContent></Card>

      {/* Edit / Create */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Ubah" : "Tambah"} · {title}</DialogTitle></DialogHeader>
          {editing && <div className="grid grid-cols-2 gap-3">
            {fields.filter(f=>!f.hideInForm).map(f => (
              <div key={f.key} className={f.colSpan === 2 ? "col-span-2" : ""}>
                <Label className="text-xs font-semibold">{f.label}</Label>
                {f.type === "file" ? (
                  <FileAttachmentField
                    value={editing[f.key] && typeof editing[f.key] === "object" ? editing[f.key] : null}
                    onChange={(v)=>setEditing({...editing, [f.key]: v})}
                    testid={`${testidPrefix}-${f.key}-upload`}
                  />
                ) : f.type === "textarea" ? (
                  <Textarea value={editing[f.key] || ""} onChange={(e)=>setEditing({...editing, [f.key]:e.target.value})} rows={3}/>
                ) : f.type === "select" ? (
                  <Select value={editing[f.key] || ""} onValueChange={(v)=>setEditing({...editing, [f.key]:v})}>
                    <SelectTrigger><SelectValue placeholder={`Pilih ${f.label}`}/></SelectTrigger>
                    <SelectContent>{f.options.map(o=><SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input type={f.type || "text"} value={editing[f.key] ?? ""} onChange={(e)=>setEditing({...editing, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value})}/>
                )}
              </div>
            ))}
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button className="bg-[#00A651] hover:bg-[#008741]" onClick={()=>mSave.mutate(editing)} disabled={mSave.isPending} data-testid={`${testidPrefix}-form-save`}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Detail */}
      <Dialog open={!!viewing} onOpenChange={(o)=>!o && setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Detail · {title}</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-3">
              {fields.map(f => (
                <div key={f.key} className="grid grid-cols-3 gap-2 py-2 border-b border-slate-100 last:border-none text-sm">
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{f.label}</div>
                  <div className="col-span-2">
                    {f.type === "file" ? (
                      viewing[f.key] && typeof viewing[f.key] === "object"
                        ? <AttachmentBadge path={viewing[f.key].path} name={viewing[f.key].name} />
                        : <span className="text-xs text-slate-400">—</span>
                    ) : f.badge ? f.badge(viewing[f.key]) : (viewing[f.key] || "—")}
                  </div>
                </div>
              ))}
              <div className="grid grid-cols-3 gap-2 py-2 text-xs text-slate-400">
                <div>Diperbarui</div><div className="col-span-2">{viewing.updated_at ? new Date(viewing.updated_at).toLocaleString("id-ID") : "—"}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
